import { api } from '../lib/api.js';
import { useFetch } from '../lib/useFetch.js';

/**
 * Settings — "Data & Settings" panel, styled after the platform mockup
 * (legacy/EHR-7-30-V27.html · openSettingsModal): email/calendar integrations
 * and org data stats.
 *
 * NOTE: the mockup is a browser-storage prototype ("your data lives in this
 * browser") — that copy is intentionally NOT reproduced here because the SPA is
 * backed by Postgres, so it would be false. Integrations are marked "soon" to
 * match their real (not-yet-wired) state.
 *
 * The former org-configuration UI (Stages / Statuses / Contact Types / Users)
 * was replaced per request; it remains in git history if it needs to return as a
 * sub-section.
 */

interface Counts {
  client?: number;
  referral_source?: number;
  staff?: number;
}
interface OrgUser {
  id: string;
}

const dot = (color: string) => (
  <span className="settings-connect-dot" style={{ background: color }} aria-hidden />
);

function ConnectButton({ color, label }: { color: string; label: string }) {
  return (
    <button className="settings-connect" type="button" title="Integration coming soon">
      {dot(color)}
      {label}
      <span className="soon">soon</span>
    </button>
  );
}

export function SettingsTab() {
  const counts = useFetch<Counts>(() => api.get('/contacts/counts'), []);
  const users = useFetch<OrgUser[]>(() => api.get('/settings/users'), []);
  const clientCount = counts.data?.client ?? null;
  const teamCount = Array.isArray(users.data) ? users.data.length : null;

  return (
    <div className="settings-panel">
      <div className="settings-head">
        <h1 className="settings-title">Data &amp; Settings</h1>
        <p className="muted">
          Connect your email and calendar, and see your organization&rsquo;s data at a glance.
        </p>
      </div>

      <section className="settings-section">
        <h3 className="settings-section-title">Email</h3>
        <p className="muted settings-section-desc">
          Connect Gmail or Outlook to send from your own address and have replies thread back to
          each client automatically. Live two-way sync is coming soon; until then you can send via
          hand-off and log replies from each client&rsquo;s <strong>Email</strong> tab.
        </p>
        <div className="settings-btn-row">
          <ConnectButton color="#ea4335" label="Connect Gmail" />
          <ConnectButton color="#0078d4" label="Connect Outlook" />
        </div>
      </section>

      <section className="settings-section">
        <h3 className="settings-section-title">Calendar</h3>
        <p className="muted settings-section-desc">
          Connect Google or Outlook Calendar to see upcoming meetings on each client and auto-sync
          events. Live two-way sync is coming soon.
        </p>
        <div className="settings-btn-row">
          <ConnectButton color="#4285f4" label="Connect Google Calendar" />
          <ConnectButton color="#0078d4" label="Connect Outlook Calendar" />
        </div>
      </section>

      <section className="settings-section">
        <h3 className="settings-section-title">Organization</h3>
        <div className="settings-stats">
          <div className="settings-stat">
            <div className="settings-stat-label">Clients</div>
            <div className="settings-stat-num">{clientCount ?? '—'}</div>
          </div>
          <div className="settings-stat">
            <div className="settings-stat-label">Team members</div>
            <div className="settings-stat-num">{teamCount ?? '—'}</div>
          </div>
          <div className="settings-stat">
            <div className="settings-stat-label">Referral sources</div>
            <div className="settings-stat-num">{counts.data?.referral_source ?? '—'}</div>
          </div>
        </div>
      </section>

      <section className="settings-section">
        <h3 className="settings-section-title">Document storage</h3>
        <p className="muted settings-section-desc">
          Attach files to any client from their <strong>Files</strong> tab. Documents are stored
          AES-encrypted in the database today; connecting Amazon S3 or Cloudflare R2 (presigned
          uploads, so bucket credentials never reach the browser) is coming soon — the Files UI is
          already wired for it.
        </p>
        <div className="settings-btn-row">
          <ConnectButton color="#ff9900" label="Connect S3 / R2" />
        </div>
      </section>
    </div>
  );
}
